import { format, parseISO, subDays } from 'date-fns';
import { formatInstantInZone, todayLocalISO } from './dateUtils';

/*
 * Day bucketing for clinical timelines.
 *
 * ⚠ THIS FILE USED TO BE EXEMPT FROM THE DATE RATCHET AND DID NOT DESERVE IT.
 * `groupByDay` keyed on `format(new Date(raw), 'yyyy-MM-dd')` — the exact
 * calendar-rollback idiom, as a GROUPING KEY rather than a display string, which
 * is worse: a wrong label is visible, a wrong bucket silently files a record
 * under the wrong DAY of the chart. `check-date-idioms.js` skipped the whole
 * file as "the ONE legitimate home for the raw idiom" (a status only
 * `dateUtils.ts` earns), so the next caller to pass a calendar day would have
 * got a silent day-shift with no gate to see it. DATE-KINDS.md §2.7.
 *
 * The exemption is now narrowed to `dateUtils.ts` and this file states the KIND
 * of the value it is bucketing, because the two kinds need OPPOSITE treatment:
 *
 *   calendar — a value whose meaning is a DAY (a `type="date"` submission, a
 *              noon/midnight-anchored column, or a naive wall-clock like
 *              `ImagingRequest.requestedDateTime`, W7). Take the DATE PART. It
 *              is timezone-invariant by construction; re-zoning it rolls it back
 *              a day in every US zone (and double-shifts the naive case).
 *   instant  — a real moment. Bucket it by the CLINIC'S day (Clinic.timezone),
 *              not the browser's, so a 9 PM Eastern record does not move onto
 *              another day for a viewer in another zone.
 */

/**
 * Which kind of value `dateOf` returns. An instant must name the zone whose day
 * it buckets by — pass `useClinicTimezone()`; there is no sensible default,
 * which is the point.
 */
export type GroupByDayKind =
  | { kind: 'calendar' }
  | { kind: 'instant'; timeZone: string };

/** A `yyyy-MM-dd` bucket id. Timezone-invariant; sorts lexically. */
export type DayKey = string;

/** The date PART of a calendar value — never re-zoned. '' when unparseable. */
function calendarDayKey(raw: string | Date): DayKey {
  if (typeof raw === 'string') {
    const day = raw.slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : '';
  }
  if (isNaN(raw.getTime())) return '';
  // A calendar day that arrives already parsed has lost its anchor. Read its UTC
  // components — that is where every calendar writer puts the day (UTC midnight
  // or noon UTC), so this is the same day the string form would have yielded.
  return raw.toISOString().slice(0, 10);
}

function dayKeyOf(raw: string | Date, opts: GroupByDayKind): DayKey {
  return opts.kind === 'calendar'
    ? calendarDayKey(raw)
    : formatInstantInZone(raw, opts.timeZone, 'yyyy-MM-dd');
}

/** Today's bucket id, in whichever frame `opts` implies. */
function todayKeyFor(opts: GroupByDayKind): DayKey {
  return opts.kind === 'instant'
    ? formatInstantInZone(new Date(), opts.timeZone, 'yyyy-MM-dd')
    : // A calendar day has no zone, so "today" is the viewer's own day — the
      // same referent every other "today" in the web app uses (todayLocalISO).
      todayLocalISO();
}

/**
 * A day-level label for a timeline section header:
 * "Today", "Yesterday", or "Tue, Apr 28, 2026".
 *
 * Takes BUCKET IDS (`yyyy-MM-dd`), not Dates, so the comparison is a string
 * compare in one frame rather than Date arithmetic across two — the old
 * signature took a `Date` and re-derived the day from it, which is how a
 * grouping key and its label could disagree.
 */
export function formatDayLabel(dayKey: DayKey, todayKey: DayKey): string {
  if (!dayKey) return '';
  if (todayKey) {
    if (dayKey === todayKey) return 'Today';
    if (dayKey === format(subDays(parseISO(todayKey), 1), 'yyyy-MM-dd')) return 'Yesterday';
  }
  return format(parseISO(dayKey), 'EEE, MMM d, yyyy');
}

/**
 * Bucket items by day, newest day first.
 *
 * `dateOf` extracts the value used for grouping AND ordering; `opts` declares
 * its KIND (see `GroupByDayKind` — the whole point of this module). Within a
 * group, items keep their input order: the caller sorts first.
 *
 * Items whose value is absent or unparseable are dropped, as before.
 */
export function groupByDay<T>(
  items: T[],
  dateOf: (item: T) => string | Date | null | undefined,
  opts: GroupByDayKind,
): { dayKey: DayKey; dayLabel: string; items: T[] }[] {
  const groups = new Map<DayKey, T[]>();
  for (const item of items) {
    const raw = dateOf(item);
    if (raw === null || raw === undefined || raw === '') continue;
    const key = dayKeyOf(raw, opts);
    if (!key) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(item);
  }
  const todayKey = todayKeyFor(opts);
  return Array.from(groups.entries())
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([dayKey, dayItems]) => ({
      dayKey,
      dayLabel: formatDayLabel(dayKey, todayKey),
      items: dayItems,
    }));
}
